// Ported from vlookup-dramatic/app/home-hero.tsx by scripts/revamp/port-design.js
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ArrowUpRight } from "../../lib/icons";
import { ButtonLink } from "../../components/button-link";
import { homeHero } from "../../lib/content";
import { VideoDialog } from "../../components/video-dialog";
import { agent } from "../../../utils/agent";
import * as API from "../../../utils/apiPath";

/* Dynamic hero (same sources the old home page used):
   - sub-copy: first paragraph of the CMS "home" page (GET pages/home) — the admin
     can change it; the design's copy is the fallback
   - video: Settings > video link (GET setting). A direct .mp4/.webm link becomes the
     background footage; a YouTube/Vimeo link opens in the "Watch the story" dialog. */
const cmsFirstParagraph = (html) => {
  if (!html) return "";
  const text = String(html).replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, "").replace(/<[^>]+>/g, "\n")
    .replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&#39;|&rsquo;/g, "’").replace(/&quot;/g, "\"");
  return text.split("\n").map((l) => l.trim()).find((l) => l.length > 60) || "";
};
const isFileVideo = (u) => /\.(mp4|webm|mov)(\?|$)/i.test(u || "");

/* homeHero.title, split so the final word stays italic. */
const TITLE_LEAD = "Career growth starts with access to the right";
const TITLE_ACCENT = "conversation.";
export function HomeHero() {
  const videoRef = useRef(null);
  const setting = useSelector((s) => s.setting);
  const [dynamicStats, setDynamicStats] = useState(null);
  const homePageData = useSelector((s) => s.homePageData) || {};
  // QA row 33 (2 Sep): the CMS "home" paragraph (old-site copy) was overriding
  // the agreed design copy on the hero's right side. Prefer the design copy;
  // flip back to CMS-first once the CMS body carries the new wording.
  const heroSub = homeHero.sub || cmsFirstParagraph(homePageData.description);
  const videoLink = (setting && setting.video_link) || "";
  const bgVideo = isFileVideo(videoLink) ? videoLink : homeHero.video.src;

  // Dynamic ticker stats from api/v1/get-stats?category=Home
  useEffect(() => {
    let alive = true;
    agent
      .get(`${API.GET_STATS}?category=Home`)
      .then((res) => {
        if (!alive) return;
        const raw = res?.data?.data ?? res?.data;
        const rows = Array.isArray(raw) ? raw : [];
        const homeRows = rows
          .filter((r) => {
            const isActive = r && r.status !== false && r.status !== 0 && r.status !== "false" && r.status !== "0";
            const isHome = !r?.category || String(r.category).trim().toLowerCase() === "home";
            return isActive && isHome && r.heading && r.count != null;
          })
          .sort((a, b) => {
            if (a.display_order != null && b.display_order != null) {
              return Number(a.display_order) - Number(b.display_order);
            }
            return 0;
          })
          .map((r) => ({
            id: r.id,
            n: r.count,
            l: r.heading,
          }));

        if (homeRows.length > 0) {
          setDynamicStats(homeRows);
        }
      })
      .catch(() => { });

    return () => {
      alive = false;
    };
  }, []);

  const ticker = (dynamicStats && dynamicStats.length > 0)
    ? dynamicStats
    : homeHero.ticker;

  // Respect prefers-reduced-motion: pause the video so the poster-like first
  // frame reads as a still. Re-sync live if the OS setting changes.
  useEffect(() => {
    const video = videoRef.current;
    if (!video)
      return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => {
      if (mq.matches)
        video.pause();
      else
        video.play().catch(() => { });
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return (
    /* Two modes from one video element:
       - below md, the footage is a real block in the flow at its native 16:9,
         so the whole frame is visible, uncropped, with no type over it at all;
         the copy starts underneath on the solid panel.
       - from md up it goes full-bleed behind the copy, which hugs the bottom
         over a scrim. */
    <section className="relative isolate flex flex-col overflow-hidden bg-brand-ink text-cream md:h-[88svh] md:max-h-[900px] md:min-h-[600px]">
      {/* ticker keyframes: styles/revamp.src.css */}

      {/* aspect-video matches the source exactly (1600x900), so on mobile the
            frame is shown whole rather than cropped. From md it drops out of the
            flow and fills the section behind the copy. */}
      <div className="relative aspect-video w-full md:absolute md:inset-0 md:aspect-auto md:-z-10" aria-hidden>
        <video ref={videoRef} autoPlay muted loop playsInline preload="metadata" width={1600} height={900} poster={homeHero.video.poster} src={bgVideo} className="h-full w-full object-cover" />
      </div>

      {/* Copy + ticker share one bottom stack so the blue shelf always
            reaches the headline, regardless of viewport height. */}
      <div className="relative mt-auto">
        <div aria-hidden className="pointer-events-none absolute inset-0 hidden hero-scrim md:block" />

        <div className="relative wrap w-full py-11 md:pb-12 md:pt-0">
          <div className="flex max-w-3xl flex-col md:grid md:max-w-none md:grid-cols-[minmax(0,1.3fr)_minmax(0,0.7fr)] md:items-end md:gap-x-14">
            <span className="inline-flex items-center gap-2.5 text-xs font-bold uppercase tracking-[0.2em] text-cream/90 md:col-start-1 md:row-start-1">
              <span className="h-px w-8 bg-brand-red" />
              A free global mentoring platform for women
            </span>
            <h1 className="mt-4 text-[2rem] font-semibold leading-[1.06] tracking-tight text-white sm:text-4xl md:col-start-1 md:row-start-2 lg:text-[2.75rem] xl:text-[3.1rem]">
              {TITLE_LEAD}{" "}
              <em className="font-normal italic text-white">
                {TITLE_ACCENT}
              </em>
            </h1>
            <p className="home-hero-sub mt-5 max-w-xl text-[15px] leading-relaxed text-cream/85 md:col-start-2 md:mt-0 md:max-w-none md:text-base">
              {heroSub}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-4 md:col-start-1 md:row-start-3">
              <ButtonLink href={homeHero.primaryCta.href} className="group h-auto w-fit gap-3 rounded-full bg-brand-red py-2 pl-7 pr-2 text-[15px] font-semibold text-white hover:bg-brand-red-dark">
                {homeHero.primaryCta.label}
                <span className="flex size-10 items-center justify-center rounded-full bg-white/20 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5">
                  <ArrowUpRight className="size-5" />
                </span>
              </ButtonLink>
              <ButtonLink href={homeHero.secondaryCta.href} variant="outline" className="h-12 rounded-full border-cream/40 bg-transparent px-7 text-[15px] font-semibold text-cream hover:bg-white/10 hover:text-white">
                {homeHero.secondaryCta.label}
              </ButtonLink>
              {videoLink && !isFileVideo(videoLink) && (<span className="inline-flex items-center gap-3 text-[13px] font-bold uppercase tracking-[0.14em] text-cream/80">
                <VideoDialog url={videoLink} triggerClassName="size-12" />
                Watch the story
              </span>)}
            </div>
          </div>
        </div>

        <div className="relative border-t border-white/10">
          <div className="overflow-hidden py-3.5">
            <div className="dHomeTickerTrack flex w-max">
              <TickerRow items={ticker} />
              <TickerRow items={ticker} hidden />
            </div>
          </div>
        </div>
      </div>
    </section>);
}
function TickerRow({ items, hidden }) {

  return (
    <ul aria-hidden={hidden || undefined} className="flex shrink-0 items-center">
      {(items || []).map((t, idx) => (
        <li key={t.id != null ? `${t.id}-${idx}` : `${t.l}-${idx}`} className="flex items-center gap-3 px-7">
          <span className="font-display text-xl font-semibold text-cream md:text-2xl">
            {t.n}
          </span>
          <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-cream/60">
            {t.l}
          </span>
          <span aria-hidden className="ml-7 block size-1.5 rotate-45 bg-brand-red" />
        </li>
      ))}
    </ul>
  );
}
